import { PlusOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { Button, Col, Empty, Modal, Row, Spin, message } from 'antd';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { Typography } from '../../components/Typography/Typography';
import { firestore } from '../../firebase/firebase';
import { FirestoreCollection } from '../../firebase/firebase.models';
import { WantedListing } from '../../models/wantedListings.model';
import { App } from '../App/App';
import { useAuth } from '../Auth/AuthContext';
import { MenuWrapper } from '../MojaTablica/components/MenuWrapper';
import { additionalNavigation } from '../MojaTablica/Menu/AdditionalNavigation';
import { MenuItems } from '../MojaTablica/Menu/Menu';
import { AppRoutes } from '../Routing/routing.model';

import { EditWantedListingModal } from './EditWantedListingModal';
import { WantedListingCard } from './WantedListingCard';

const HeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
`;

export const MyWantedListings: React.FC = () => {
  const { currentUser } = useAuth();
  const history = useHistory();
  const { t } = useTranslation('wanted-listings');
  const [listings, setListings] = useState<WantedListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [userCompanyId, setUserCompanyId] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string>('');
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingListing, setEditingListing] = useState<WantedListing | null>(null);

  const onClick: MenuProps['onClick'] = (e) => {
    additionalNavigation(e.key);
    history.push(e.key);
  };

  useEffect(() => {
    if (!currentUser) return;

    const fetchUserCompanyId = async () => {
      try {
        const userDoc = await firestore.collection(FirestoreCollection.USERS).doc(currentUser.uid).get();
        const userData = userDoc.data();

        if (userData?.companyId) {
          setUserCompanyId(userData.companyId);

          // Pobierz nazwę firmy - wrapped in try/catch for permission issues
          try {
            const companyDoc = await firestore.collection(FirestoreCollection.COMPANIES).doc(userData.companyId).get();
            const companyData = companyDoc.data();
            if (companyData?.company?.nazwa) {
              setCompanyName(companyData.company.nazwa);
            }
          } catch (companyError) {
            console.warn(
              'Could not fetch company name - permission denied. This is normal if user is not in company hasAccess array.'
            );
            // Set a default company name or leave empty
            setCompanyName(t('LABELS.YOUR_COMPANY'));
          }
        } else {
          // If no company ID, set loading to false
          setLoading(false);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        // Set loading to false to show empty state
        setLoading(false);
      }
    };

    fetchUserCompanyId();
  }, [currentUser]);

  useEffect(() => {
    if (!userCompanyId) return;

    const unsubscribe = firestore
      .collection('wantedListings')
      .where('companyId', '==', userCompanyId)
      .where('status', '==', 'active')
      .orderBy('createdAt', 'desc')
      .onSnapshot(
        (snapshot) => {
          const data = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          })) as WantedListing[];

          // Filtruj tylko ogłoszenia tej firmy i tylko aktywne
          const companyListings = data.filter(
            (listing) => listing.companyId === userCompanyId && listing.status === 'active'
          );
          setListings(companyListings);
          setLoading(false);
        },
        (err) => {
          console.error('Error fetching my wanted listings:', err);
          setLoading(false);
        }
      );

    return () => unsubscribe();
  }, [userCompanyId]);

  const handleEdit = (listing: WantedListing) => {
    setEditingListing(listing);
    setIsEditModalVisible(true);
  };

  const handleEditModalClose = () => {
    setIsEditModalVisible(false);
    setEditingListing(null);
  };

  const handleDelete = async (listing: WantedListing) => {
    Modal.confirm({
      title: t('DELETE_MODAL.TITLE'),
      content: t('DELETE_MODAL.CONTENT'),
      okText: t('DELETE_MODAL.CONFIRM'),
      cancelText: t('DELETE_MODAL.CANCEL'),
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await firestore.collection('wantedListings').doc(listing.id).update({
            status: 'deleted',
          });
          message.success(t('MESSAGES.DELETE_SUCCESS'));
        } catch (error) {
          console.error('Error deleting listing:', error);
          message.error(t('MESSAGES.DELETE_ERROR'));
        }
      },
    });
  };

  const handleAddNew = () => {
    history.push(AppRoutes.WANTED_LISTINGS);
  };

  return (
    <App>
      <div className="flex">
        <MenuWrapper>
          <MenuItems
            onClick={onClick}
            defaultSelectedKeys={[AppRoutes.MOJA_TABLICA_WANTED_LISTINGS]}
            defaultOpenKeys={['sub1']}
          />
        </MenuWrapper>

        <div style={{ flex: 1, padding: '40px' }}>
          {loading ? (
            <Spin size="large" style={{ display: 'block', margin: '50px auto' }} />
          ) : (
            <>
              <HeaderWrapper>
                <Typography.H6>{t('MY_LISTINGS_TITLE')}</Typography.H6>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAddNew}>
                  {t('ADD_NEW_LISTING')}
                </Button>
              </HeaderWrapper>

              {listings.length === 0 ? (
                <Empty description={t('NO_LISTINGS')} style={{ margin: '80px 0' }}>
                  <Button type="primary" onClick={handleAddNew}>
                    {t('FIRST_LISTING')}
                  </Button>
                </Empty>
              ) : (
                <Row gutter={[24, 24]}>
                  {listings.map((listing) => (
                    <Col key={listing.id} xs={24} md={12} lg={8}>
                      <WantedListingCard
                        listing={listing}
                        companyName={companyName}
                        onOfferClick={() => {
                          // Funkcja wyłączona dla własnych ogłoszeń
                        }}
                        onEdit={() => handleEdit(listing)}
                        onDelete={() => handleDelete(listing)}
                        isOwner={true}
                      />
                    </Col>
                  ))}
                </Row>
              )}
            </>
          )}
        </div>
      </div>

      {isEditModalVisible && editingListing && (
        <EditWantedListingModal visible={isEditModalVisible} onClose={handleEditModalClose} listing={editingListing} />
      )}
    </App>
  );
};
